import { clientLogger } from '@/lib/clientLogger';
'use client';

import { createContext, useContext, useState, useEffect, useMemo, useRef, ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService } from '@/lib/api';
import { getStorageItem, setStorageItem } from '@/lib/capacitor';
import { SELECTED_COMPANY_DATA_STORAGE_KEY } from '@/lib/dateUtils';
import { CompanyBasicDTO } from '@/types';
import { useAuth } from './useAuth';
import { useGlobalOrganization } from './useGlobalOrganization';
import { useCurrentUser } from './useCurrentUser';

interface GlobalCompanyContextType {
    companies: CompanyBasicDTO[];
    isLoading: boolean;
    selectedCompanyId: string;
    setSelectedCompanyId: (id: string) => void;
    selectedCompany: CompanyBasicDTO | null;
    companyTimezone: string | undefined;
}

const GlobalCompanyContext = createContext<GlobalCompanyContextType | undefined>(undefined);

interface GlobalCompanyProviderProps {
    children: ReactNode;
}

interface PersistedCompanyData {
    id: string;
    timezone?: string;
}

function parsePersistedCompanyData(raw: string | null): PersistedCompanyData | null {
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw) as unknown;
        if (parsed && typeof parsed === 'object' && 'id' in parsed) {
            return parsed as PersistedCompanyData;
        }
        return null;
    } catch {
        return null;
    }
}

export function GlobalCompanyProvider({ children }: GlobalCompanyProviderProps) {
    const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
    const [persistedCompanyId, setPersistedCompanyId] = useState<string | null>(null);
    const [storageLoaded, setStorageLoaded] = useState(false);
    const [cachedTimezone, setCachedTimezone] = useState<string | undefined>(undefined);
    const hasInitialized = useRef(false);
    const previousCompanyId = useRef<string>('');
    const queryClient = useQueryClient();

    const { isAuthenticated } = useAuth();
    const { orgReady } = useGlobalOrganization();
    const { isAdmin, isLoading: roleLoading } = useCurrentUser();

    const { data: companies = [], isLoading } = useQuery({
        queryKey: ['companies'],
        queryFn: () => apiService.getCompanies(),
        enabled: isAuthenticated && orgReady,
        staleTime: 30 * 60 * 1000, // 30 minutes — company config (incl. timezone) rarely changes
    });

    useEffect(() => {
        let isMounted = true;

        if (!isAuthenticated || !orgReady) {
            hasInitialized.current = false;
            previousCompanyId.current = '';
            setSelectedCompanyId('');
            setPersistedCompanyId(null);
            setStorageLoaded(false);
            return () => {
                isMounted = false;
            };
        }

        setStorageLoaded(false);

        void getStorageItem(SELECTED_COMPANY_DATA_STORAGE_KEY)
            .then((raw) => {
                if (!isMounted) return;

                const data = parsePersistedCompanyData(raw);
                setPersistedCompanyId(data?.id ?? null);
                setCachedTimezone(data?.timezone || undefined);
                setStorageLoaded(true);
            })
            .catch((error) => {
                clientLogger.error('Failed to load persisted company selection:', error);
                if (!isMounted) return;
                setPersistedCompanyId(null);
                setStorageLoaded(true);
            });

        return () => {
            isMounted = false;
        };
    }, [isAuthenticated, orgReady]);

    // Default to the first available company on initial load.
    // Non-admin users are also forced back to a valid company if their selection becomes empty.
    useEffect(() => {
        if (roleLoading || !storageLoaded || companies.length === 0) {
            return;
        }

        const hasValidSelectedCompany = companies.some((company) => company.id === selectedCompanyId);
        const hasValidPersistedCompany = persistedCompanyId !== null && companies.some((company) => company.id === persistedCompanyId);
        const canUseAdminAllCompanies = isAdmin && persistedCompanyId === '';

        if (!hasInitialized.current) {
            if (hasValidPersistedCompany || canUseAdminAllCompanies) {
                setSelectedCompanyId(persistedCompanyId ?? '');
            } else {
                setSelectedCompanyId(companies[0].id);
            }

            hasInitialized.current = true;
            return;
        }

        if (!isAdmin && !hasValidSelectedCompany) {
            setSelectedCompanyId(companies[0].id);
            return;
        }

        if (isAdmin && selectedCompanyId && !hasValidSelectedCompany) {
            setSelectedCompanyId(companies[0].id);
        }
    }, [companies, isAdmin, persistedCompanyId, roleLoading, selectedCompanyId, storageLoaded]);

    const selectedCompany = useMemo(() => {
        if (!selectedCompanyId) return null;
        return companies.find((c: CompanyBasicDTO) => c.id === selectedCompanyId) || null;
    }, [companies, selectedCompanyId]);

    const companyTimezone = useMemo(() => {
        if (!selectedCompanyId) return undefined;
        return selectedCompany?.timezone || cachedTimezone;
    }, [selectedCompanyId, selectedCompany, cachedTimezone]);

    // Mirror company IANA timezone into ApiService for synchronous VoIP headers (X-Timezone).
    useEffect(() => {
        apiService.setCompanyTimezone(companyTimezone?.trim() || null);
    }, [companyTimezone]);

    // Sync selected company ID to the API service so all requests include x-company-id header.
    // Also invalidate all queries when the company changes so stale data isn't served from cache.
    useEffect(() => {
        apiService.setCompanyId(selectedCompanyId || null);

        if (storageLoaded && hasInitialized.current) {
            // Persist only the ID here; timezone is written separately once selectedCompany resolves
            // so we always have the freshest timezone from the API alongside the ID.
            const currentTimezone = cachedTimezone;
            void setStorageItem(
                SELECTED_COMPANY_DATA_STORAGE_KEY,
                JSON.stringify({ id: selectedCompanyId, timezone: currentTimezone }),
            ).catch((error) => {
                clientLogger.error('Failed to persist company selection:', error);
            });
        }

        // Only invalidate on actual company changes (not on initial mount)
        if (previousCompanyId.current !== '' || hasInitialized.current) {
            if (previousCompanyId.current !== selectedCompanyId) {
                queryClient.invalidateQueries();
            }
        }
        previousCompanyId.current = selectedCompanyId;
    }, [selectedCompanyId, queryClient, storageLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

    // Derive timezone from selected company; update cache whenever it resolves from the API.
    // The cachedTimezone fallback ensures components see the correct timezone immediately on
    // cold start, before the API response arrives.
    useEffect(() => {
        if (!selectedCompany) return;
        const tz = selectedCompany.timezone || undefined;
        setCachedTimezone(tz);
        // Persist the paired (id + timezone) atomically so they are always in sync
        void setStorageItem(
            SELECTED_COMPANY_DATA_STORAGE_KEY,
            JSON.stringify({ id: selectedCompany.id, timezone: tz }),
        ).catch(() => {});
    }, [selectedCompany]);

    const value = useMemo(() => ({
        companies,
        isLoading,
        selectedCompanyId,
        setSelectedCompanyId,
        selectedCompany,
        companyTimezone,
    }), [companies, isLoading, selectedCompanyId, selectedCompany, companyTimezone]);

    return (
        <GlobalCompanyContext.Provider value={value}>
            {children}
        </GlobalCompanyContext.Provider>
    );
}

export function useGlobalCompany() {
    const context = useContext(GlobalCompanyContext);
    if (context === undefined) {
        throw new Error('useGlobalCompany must be used within a GlobalCompanyProvider');
    }
    return context;
}

export function useCompanyTimezone(): string | undefined {
    const { companyTimezone } = useGlobalCompany();
    return companyTimezone;
}

export function useTimezoneMismatch(): { isMismatch: boolean; userTz: string; companyTz: string | undefined } {
    const { companyTimezone } = useGlobalCompany();
    const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const isMismatch = !!companyTimezone && companyTimezone !== userTz;
    return { isMismatch, userTz, companyTz: companyTimezone };
}
